import type { Type } from '@dce-front/onewebapp-utils';
import { useCallback, useContext, type JSX } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import { virtualKeyboardContext } from './VirtualKeyboardContext';
import {
  addCharacter,
  deleteCharacter,
  dispatchDecrement,
  dispatchIncrement,
  updateMode,
} from './actions';
import {
  CLEAR_KEY,
  INITIAL_STATE,
  LOWER_KEY,
  NEXT_KEY,
  PREV_KEY,
  SPECIALS_KEY,
  UPPER_KEY,
} from './const';
import { Mode } from './types';

export type VirtualKeyboardContainerProps = {
  isDisabled?: boolean;
  isPolish?: boolean;
  layout?: 'normal' | 'polish' | 'big' | string[];
  maxInputValue?: number;
  selectedId?: string;
  type?: Type;
};

export const VirtualKeyboardContainer = ({
  maxInputValue = Number.MAX_VALUE,
  ...rest
}: VirtualKeyboardContainerProps): JSX.Element => {
  const { state = INITIAL_STATE, dispatch } = useContext(
    virtualKeyboardContext,
  );
  const { inputValue, mode } = state;

  const onKeyPress = useCallback(
    (id: string) => {
      switch (id) {
        case UPPER_KEY.id:
          return dispatch(updateMode(Mode.UPPERCASE));

        case LOWER_KEY.id:
          return dispatch(updateMode(Mode.LOWERCASE));

        case SPECIALS_KEY.id:
          return dispatch(updateMode(Mode.SPECIALS));

        case CLEAR_KEY.id:
          return dispatch(deleteCharacter());

        case NEXT_KEY.id:
          return dispatch(dispatchIncrement);

        case PREV_KEY.id:
          return dispatch(dispatchDecrement);

        default:
          return dispatch(addCharacter(id, rest.isPolish ?? false));
      }
    },
    [dispatch, rest.isPolish],
  );

  const isAddCharDisabled = inputValue.length >= maxInputValue;

  return (
    <VirtualKeyboard
      {...rest}
      onKeyPress={onKeyPress}
      mode={mode}
      isAddCharDisabled={isAddCharDisabled}
    />
  );
};
